import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { IconX, IconPencil, IconCheck, IconPlus } from "@tabler/icons-react";
import urlConstant from "../../constants/urlConstants";
import apiClient from "../../lib/api";
import SnackState from "../../shared-components/snackbar/snackState";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import { trackPromise } from "react-promise-tracker";
import { lightPalette } from "../../theme-layout/palette";

const MAX_NAME_LENGTH = 50;

const StateCityModal = ({ open, onClose, onSave, initialData = {} }) => {
  const [stateName, setStateName] = useState("");
  const [stateNameError, setStateNameError] = useState("");
  const [cities, setCities] = useState([]);
  const [originalCities, setOriginalCities] = useState([]);
  const [newCityName, setNewCityName] = useState("");
  const [newCityError, setNewCityError] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const currentUser = useSelector(selectUserDetail)?.userId;

  useEffect(() => {
    if (initialData?.id && initialData?.stateName && initialData?.cities) {
      setStateName(initialData.stateName || "");
      const parsedCities = initialData.cities.map((city) => ({
        id: city.id || 0,
        name: city.name || city.cityName || "",
        isDeleted: false,
      }));
      setCities(parsedCities);
      setOriginalCities(parsedCities);
    } else {
      setStateName("");
      setCities([]);
      setOriginalCities([]);
    }
    setEditIndex(null);
    setNewCityName("");
    setStateNameError("");
    setNewCityError("");
  }, [initialData, open]);

  const handleAddCity = () => {
    const name = newCityName.trim();
    if (!name) {
      setNewCityError("City name is required");
      return;
    } else if (name.length > MAX_NAME_LENGTH) {
      setNewCityError("City name must be under 100 characters");
      return;
    } else if (
      cities.some(
        (c) =>
          !c.isDeleted && c.name.trim().toLowerCase() === name.toLowerCase(),
      )
    ) {
      setNewCityError("City name must be unique");
      return;
    }

    setCities([...cities, { id: 0, name, isDeleted: false }]);
    setNewCityName("");
    setNewCityError("");
  };

  const handleEditCity = (index) => {
    setEditIndex(index);
  };

  const handleCityChange = (index, value) => {
    const updated = [...cities];
    updated[index].name = value;
    setCities(updated);
  };

  const handleRemoveCity = (index) => {
    const updated = [...cities];
    updated[index].isDeleted = true;
    setCities(updated);
    if (editIndex === index) setEditIndex(null);
  };

  const handleSave = () => {
    // Validate state name
    if (!stateName.trim()) {
      setStateNameError("State name is required");
      return;
    } else if (stateName.trim().length > MAX_NAME_LENGTH) {
      setStateNameError("State name must be under 50 characters");
      return;
    }

    const activeCities = cities.filter((city) => !city.isDeleted);

    const nameSet = new Set();
    for (let i = 0; i < activeCities.length; i++) {
      const name = activeCities[i].name.trim();
      if (!name) {
        setNewCityError(`City ${i + 1} name is required`, "error");
        return;
      }
      if (name.length > MAX_NAME_LENGTH) {
        setNewCityError(
          `City ${i + 1} name must be under 50 characters`,
          "error",
        );
        return;
      }
      const lowerName = name.toLowerCase();
      if (nameSet.has(lowerName)) {
        setNewCityError(`Duplicate city name: ${name}`, "error");
        return;
      }
      nameSet.add(lowerName);
    }

    let payload = {};

    if (initialData?.id) {
      const cityUpdateRequests = [];

      const currentCityMap = new Map(
        cities.map((c) => [c.id || `new-${c.name}`, c]),
      );
      const originalCityMap = new Map(
        originalCities.map((c) => [c.id || `new-${c.name}`, c]),
      );

      originalCities.forEach((orig) => {
        const key = orig.id || `new-${orig.name}`;
        const match = currentCityMap.get(key);
        if (match && !match.isDeleted) {
          cityUpdateRequests.push({
            unqGuid: match.id,
            cityName: match.name,
          });
        }
      });

      cities.forEach((c) => {
        const key = c.id || `new-${c.name}`;
        const isNew = !originalCityMap.has(key);
        if (isNew && !c.isDeleted) {
          cityUpdateRequests.push({
            unqGuid: "00000000-0000-0000-0000-000000000000",
            cityName: c.name,
          });
        }
      });

      payload = {
        unqGuid: initialData.id,
        stateName: stateName.trim(),
        userId: currentUser,
        cityUpdateRequests,
      };

      trackPromise(
        apiClient
          .put(urlConstant.State.UpdateStateAndCity, payload)
          .then((response) => {
            if (
              response?.statusCode === 200 &&
              response?.status !== "Failure"
            ) {
              showSnackbar(response?.message, "success");
              onSave();
            } else {
              showSnackbar(response?.message || "Operation failed", "error");
            }
          })
          .catch((error) => {
            showSnackbar(
              error?.response?.data?.message || "Server error during update",
              "error",
            );
          }),
      );
    } else {
      const cityInsertRequests = activeCities.map((c) => ({
        cityName: c.name,
      }));
      payload = {
        stateName: stateName.trim(),
        userId: currentUser,
        cityInsertRequests,
      };

      trackPromise(
        apiClient
          .post(urlConstant.State.CreateStateAndCity, payload)
          .then((response) => {
            if (
              response?.statusCode === 200 &&
              response?.status !== "Failure"
            ) {
              showSnackbar(response?.message, "success");
              onSave();
            } else {
              setStateNameError(response?.message);
              // showSnackbar(response?.message || "Operation failed", "error");
            }
          })
          .catch((error) => {
            showSnackbar(
              error?.response?.data?.message || "Server error during update",
              "error",
            );
          }),
      );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {initialData?.id ? "Edit State - City" : "Add State - City"}
          <IconButton aria-label="close" onClick={onClose} sx={{ padding: 0 }}>
            <IconX />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="State Name"
            fullWidth
            value={stateName}
            onChange={(e) => {
              setStateName(e.target.value);
              setStateNameError("");
            }}
            error={!!stateNameError}
            helperText={stateNameError}
            sx={{ my: 0 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5">Add City</Typography>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              label="City Name"
              value={newCityName}
              onChange={(e) => {
                setNewCityName(e.target.value);
                setNewCityError("");
              }}
              error={!!newCityError}
              helperText={newCityError}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleAddCity}
              startIcon={<IconPlus stroke={1.5} />}
            >
              Add City
            </Button>
          </Stack>

          <Typography
            variant="subtitle1"
            my={1.5}
            padding={1.5}
            borderBottom={"1px solid"}
            borderColor={lightPalette.primary.lighter}
          >
            Cities
          </Typography>
          {cities.map((city, index) =>
            city.isDeleted ? null : (
              <React.Fragment key={index}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  p={1.5}
                  borderBottom={"1px solid"}
                  borderColor={lightPalette.primary.lightest1}
                >
                  {editIndex === index ? (
                    (() => {
                      const trimmed = city.name.trim();
                      const isEmpty = !trimmed;
                      const isTooLong = trimmed.length > MAX_NAME_LENGTH;
                      const isDuplicate = cities.some(
                        (c, i) =>
                          !c.isDeleted &&
                          i !== index &&
                          c.name.trim().toLowerCase() === trimmed.toLowerCase(),
                      );

                      const errorText = isEmpty
                        ? "City name is required"
                        : isTooLong
                          ? "City name cannot exceed 50 characters"
                          : isDuplicate
                            ? "City name must be unique"
                            : "";

                      return (
                        <>
                          <TextField
                            value={city.name}
                            onChange={(e) =>
                              handleCityChange(index, e.target.value)
                            }
                            fullWidth
                            error={!!errorText}
                            helperText={errorText}
                            inputProps={{ maxLength: MAX_NAME_LENGTH }}
                          />
                          <IconButton
                            onClick={() => {
                              if (!errorText) {
                                const updated = [...cities];
                                updated[index].name = trimmed;
                                setCities(updated);
                                setEditIndex(null);
                              }
                            }}
                            disabled={!!errorText}
                          >
                            <IconCheck size={18} />
                          </IconButton>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      <Typography sx={{ flexGrow: 1 }}>{city.name}</Typography>
                      <IconButton onClick={() => handleEditCity(index)}>
                        <IconPencil size={18} />
                      </IconButton>
                    </>
                  )}

                  <IconButton onClick={() => handleRemoveCity(index)}>
                    <IconX size={18} />
                  </IconButton>
                </Stack>
              </React.Fragment>
            ),
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <CommonSnackBar
        snackObj={snack}
        closeSnack={closeSnack}
        duration={2000}
      />
    </>
  );
};

export default StateCityModal;
